// klm-client-frontend/src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getCSRFToken } from './store/api/base';
import { clearError, clearSuccess } from './store/slices/uiSlice';
import { fetchWishlist } from './store/slices/wishlistSlice';
import MainLayout from './components/layout/mainLayout';
import { CartProvider } from './context/cartContext';
import MaintenanceChecker from './components/common/MaintenanceChecker';

// Pages
import Home from './pages/Home';
import AuthPage from './pages/authPage';
import Dashboard from './pages/dashboard';
import Profile from './pages/profile';
import NotFound from './pages/notFound';
import CartPage from './pages/cartPage';
import Checkout from './pages/checkout';
import Wishlist from './pages/wishlist';
import Payment from './pages/payment';
import PaymentCallback from './pages/paymentCallback';
import OrderSuccess from './pages/orderSuccess';
import Orders from './pages/orders';
import OrderDetail from './pages/orderDetail';

// Product Pages
import ProductListing from './pages/productListing';
import ProductDetail from './pages/productDetail';
import Categories from './pages/categories';
import CategoryProducts from './pages/categoryProducts';
import Brands from './pages/brands';
import BrandProducts from './pages/brandProducts';
import NewArrivals from './pages/newArrivals';
import SearchResults from './pages/searchResults';

// Policy & Info Pages
import TermsOfService from './pages/termsOfService';
import PrivacyPolicy from './pages/privacyPolicy';
import RefundPolicy from './pages/refundPolicy';
import ShippingPolicy from './pages/shippingPolicy';
import ContactUs from './pages/contactUs';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useSelector(state => state.auth);
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" />;
  }
  
  return children;
};

function App() {
  const dispatch = useDispatch();
  const { error, success } = useSelector(state => state.ui);
  const { isAuthenticated } = useSelector(state => state.auth);

  useEffect(() => {
    getCSRFToken();
  }, []);

  // Fetch wishlist once on login — populates wishlistStatus for all WishlistButtons
  // This eliminates the per-product checkWishlistItem calls that cause 429s
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchWishlist());
    }
  }, [isAuthenticated, dispatch]);
  
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
    
    if (success) {
      toast.success(success);
      dispatch(clearSuccess());
    }
  }, [error, success, dispatch]);
  
  return (
   <>
    {/* Sticky WhatsApp Button - Global */}
  
    <MaintenanceChecker>

       <a
  href="https://wa.me/917995004136"
  target="_blank"
  rel="noopener noreferrer"
  className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full h-14 w-14 flex items-center justify-center shadow-lg transition-transform hover:scale-110"
  title="Chat on WhatsApp"
>
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="h-7 w-7 fill-white">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.472 2.027 7.774L0 32l8.476-2.003A15.929 15.929 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.268a13.22 13.22 0 01-6.748-1.848l-.483-.287-4.998 1.181 1.234-4.874-.317-.502A13.22 13.22 0 012.732 16C2.732 8.686 8.686 2.732 16 2.732S29.268 8.686 29.268 16 23.314 29.268 16 29.268zm7.274-9.66c-.398-.199-2.354-1.161-2.72-1.294-.365-.133-.631-.199-.897.199-.265.398-1.029 1.294-1.261 1.56-.232.265-.465.298-.863.1-.398-.199-1.681-.619-3.202-1.977-1.183-1.057-1.981-2.362-2.213-2.76-.232-.398-.025-.613.174-.811.179-.178.398-.465.597-.697.199-.232.265-.398.398-.664.133-.265.066-.497-.033-.697-.1-.199-.897-2.162-1.229-2.96-.324-.778-.653-.673-.897-.686l-.764-.013c-.265 0-.697.1-.1063.497-.365.398-1.395 1.362-1.395 3.322 0 1.96 1.428 3.854 1.627 4.12.199.265 2.81 4.291 6.812 6.019.952.411 1.695.657 2.274.841.955.304 1.824.261 2.511.158.766-.114 2.354-.962 2.686-1.891.332-.929.332-1.726.232-1.891-.1-.166-.365-.265-.763-.464z"/>
  </svg>
</a>
      <Router>
        <CartProvider>
          <Routes>

            {/* Main */}
            <Route path="/" element={
              <MainLayout>
                <Home />
              </MainLayout>
            } />
            
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/signup" element={<Navigate to="/auth" replace />} />
            
            {/* Products */}
            <Route path="/products" element={
              <MainLayout>
                <ProductListing />
              </MainLayout>
            } />
            
            <Route path="/product/:productId" element={
              <MainLayout>
                <ProductDetail />
              </MainLayout>
            } />
            
            <Route path="/categories" element={
              <MainLayout>
                <Categories />
              </MainLayout>
            } />
            
            <Route path="/category/:category" element={
              <MainLayout>
                <CategoryProducts />
              </MainLayout>
            } />
            
            <Route path="/brands" element={
              <MainLayout>
                <Brands />
              </MainLayout>
            } />
            
            <Route path="/brand/:brand" element={
              <MainLayout>
                <BrandProducts />
              </MainLayout>
            } />
            
            <Route path="/new-arrivals" element={
              <MainLayout>
                <NewArrivals />
              </MainLayout>
            } />
            
            <Route path="/search" element={
              <MainLayout>
                <SearchResults />
              </MainLayout>
            } />
            
            {/* Cart */}
            <Route path="/cart" element={
              <MainLayout>
                <CartPage />
              </MainLayout>
            } />

            {/* Protected Routes */}
            <Route path="/wishlist" element={
              <ProtectedRoute>
                <MainLayout>
                  <Wishlist />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/checkout" element={
              <ProtectedRoute>
                <MainLayout>
                  <Checkout />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/payment/:orderId" element={
              <ProtectedRoute>
                <MainLayout>
                  <Payment />
                </MainLayout>
              </ProtectedRoute>
            } />

            {/* PhonePe redirects here after payment */}
            <Route path="/payment/callback/:orderId" element={
              <ProtectedRoute>
                <MainLayout>
                  <PaymentCallback />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/order-success/:orderId" element={
              <ProtectedRoute>
                <MainLayout>
                  <OrderSuccess />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/orders" element={
              <ProtectedRoute>
                <MainLayout>
                  <Orders />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/order/:orderId" element={
              <ProtectedRoute>
                <MainLayout>
                  <OrderDetail />
                </MainLayout>
              </ProtectedRoute>
            } />

            <Route path="/dashboard" element={
              <ProtectedRoute>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <MainLayout>
                  <Profile />
                </MainLayout>
              </ProtectedRoute>
            } />

            {/* Policy & Info Pages — required for PhonePe verification */}
            <Route path="/terms-of-service" element={
              <MainLayout>
                <TermsOfService />
              </MainLayout>
            } />

            <Route path="/privacy-policy" element={
              <MainLayout>
                <PrivacyPolicy />
              </MainLayout>
            } />

            <Route path="/refund-policy" element={
              <MainLayout>
                <RefundPolicy />
              </MainLayout>
            } />

            <Route path="/shipping-policy" element={
              <MainLayout>
                <ShippingPolicy />
              </MainLayout>
            } />

            <Route path="/contact" element={
              <MainLayout>
                <ContactUs />
              </MainLayout>
            } />

            {/* 404 */}
            <Route path="*" element={
              <MainLayout>
                <NotFound />
              </MainLayout>
            } />

          </Routes>
        </CartProvider>
      </Router>
    </MaintenanceChecker>
</>
    
  );
}

export default App;